import { useState, useEffect, useCallback, useMemo } from 'react';
import { unmarshall } from '@aws-sdk/util-dynamodb';
import { useAuthContext } from '../../contexts/AuthContext';
import { getUserConfigurations } from '../../server/userConfigurationsService';
import { getClassIcon, DEFAULT_WIDGET_DIMENSIONS } from '../classIconMapping';
import { AUTH_USER_POOL_ID } from '../config/configs';

const APPLICATION_NAME = 'Buildings';
const MOCK_MODE = process.env.REACT_APP_MOCK_WIDGETS === 'true';

/**
 * Fake widgets used when REACT_APP_MOCK_WIDGETS=true.
 * Simulates what the API would return for a user with saved widgets.
 */
const MOCK_WIDGETS = [
  {
    id: 'customWidget_mock_1',
    name: 'PM2.5 - Zona Centro',
    description: 'Análise de qualidade do ar na zona centro',
    class: 'Air Pollution Control',
    items: [{ id: 'sensor-1', name: 'Sensor A - Centro' }],
    metrics: ['pm2_5'],
    chartType: 'columnsChartApex',
    visualizationType: 'aggregated',
    period: 'DAILY',
    dates: { start: '2026-01-01T00:00:00Z', end: '2026-03-01T00:00:00Z' },
  },
  {
    id: 'customWidget_mock_2',
    name: 'Ruído - Avenida Principal',
    description: 'Monitorização de ruído na avenida principal',
    class: 'Noise Pollution Control',
    items: [{ id: 'sensor-2', name: 'Sensor Ruído Av.' }],
    metrics: ['noise_level'],
    chartType: 'lineChartApex',
    visualizationType: 'timeSeries',
    period: 'HOURLY',
    dates: { start: '2026-02-01T00:00:00Z', end: '2026-03-01T00:00:00Z' },
  },
  {
    id: 'customWidget_mock_3',
    name: 'Rota Bus 415',
    description: 'Desempenho da rota de autocarro 415',
    class: 'Bus Route',
    items: [{ id: 'route-415', name: 'Bus 415' }],
    metrics: ['on_time_percentage'],
    chartType: 'gaugeChart',
    visualizationType: 'aggregated',
    period: 'DAILY',
    dates: { start: '2026-01-15T00:00:00Z', end: '2026-03-01T00:00:00Z' },
  },
  {
    id: 'customWidget_mock_4',
    name: 'Meteo - Cascais',
    description: 'Previsão meteorológica para Cascais',
    class: 'Weather Service',
    items: [{ id: 'weather-1', name: 'Estação Cascais' }],
    metrics: ['temperature', 'humidity'],
    chartType: 'areaChartApex',
    visualizationType: 'timeSeries',
    period: 'HOURLY',
    dates: { start: '2026-02-15T00:00:00Z', end: '2026-03-01T00:00:00Z' },
  },
  {
    id: 'customWidget_mock_5',
    name: 'Qualidade Água - Praia',
    description: 'Índice de qualidade da água na praia',
    class: 'Water Quality',
    items: [{ id: 'water-1', name: 'Sensor Praia' }],
    metrics: ['ph', 'turbidity'],
    chartType: 'columnsChartApex',
    visualizationType: 'aggregated',
    period: 'DAILY',
    dates: { start: '2026-01-01T00:00:00Z', end: '2026-03-01T00:00:00Z' },
  },
];

/**
 * Custom hook that fetches the logged-in user's saved widgets from the
 * user-configurations API and returns them in a format ready for use
 * in the VideoWall grid toolbox.
 *
 * Widgets are read-only — the user cannot create or delete them here.
 * They are managed in the other platform and only consumed here.
 *
 * @returns {{
 *   widgets: Array,      // Parsed widget catalog for the toolbox
 *   loading: boolean,    // Whether the API call is in progress
 *   error: string|null,  // Error message, if any
 *   refetch: Function    // Manually re-fetch widgets
 * }}
 */
const useUserWidgets = () => {
  const { user } = useAuthContext();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(!MOCK_MODE);
  const [error, setError] = useState(null);

  // Resolve the username from the user object (decoded IdToken from Cognito)
  const username = useMemo(() => {
    if (user?.['cognito:username']) return user['cognito:username'];
    if (user?.username) return user.username;
    if (user?.Username) return user.Username;
    if (user?.email) return user.email;
    return null;
  }, [user]);

  /**
   * Parse the raw custom widgets array from the API response into
   * the catalog format consumed by VideoWallGrid.
   */
  const parseWidgets = useCallback((rawWidgets) => {
    if (!Array.isArray(rawWidgets)) return [];

    return rawWidgets
      .filter(widget => widget && widget.id)
      .map(widget => {
        const classIcon = getClassIcon(widget.class);

        return {
          // Identification
          id: widget.id,
          name: widget.name || 'Widget sem nome',
          description: widget.description || '',
          class: widget.class || 'Unknown',

          // Display
          icon: classIcon.icon,
          color: classIcon.color,

          // Grid dimensions (sensible defaults)
          w: DEFAULT_WIDGET_DIMENSIONS.w,
          h: DEFAULT_WIDGET_DIMENSIONS.h,
          minW: DEFAULT_WIDGET_DIMENSIONS.minW,
          minH: DEFAULT_WIDGET_DIMENSIONS.minH,
          maxW: DEFAULT_WIDGET_DIMENSIONS.maxW,
          maxH: DEFAULT_WIDGET_DIMENSIONS.maxH,

          // Preserve ALL original widget fields for chart rendering.
          // This captures items, metrics, chartType, visualizationType,
          // period, dates, series, data, labels, options — anything the
          // Buildings platform stored with this widget.
          meta: { ...widget },
        };
      });
  }, []);

  /**
   * Convert a DynamoDB-typed value to plain JavaScript.
   * Same logic as Buildings' convertDynamoDBToJS.
   */
  const convertDynamoDBToJS = useCallback((dynamoData) => {
    if (!dynamoData) return null;
    try {
      if (Array.isArray(dynamoData)) {
        return dynamoData.map(item => {
          if (item && item.M && typeof item.M === 'object') return unmarshall(item.M);
          if (item && (item.S || item.N || item.L || item.M || item.BOOL)) return unmarshall(item);
          return item;
        });
      }
      if (dynamoData.L && Array.isArray(dynamoData.L)) {
        return dynamoData.L.map(item => {
          if (item.M) return unmarshall(item.M);
          return unmarshall(item);
        });
      }
      if (dynamoData.M && typeof dynamoData.M === 'object') {
        return unmarshall(dynamoData.M);
      }
      return unmarshall(dynamoData);
    } catch (error) {
      console.error('[useUserWidgets] Error converting DynamoDB to JS:', error);
      return null;
    }
  }, []);

  /**
   * Extract custom widgets from the API response.
   * The response is a flat map of DynamoDB-typed values keyed by config name.
   */
  const extractWidgetsFromResponse = useCallback((data, username) => {
    try {
      if (!data) return [];

      const configMap = Array.isArray(data) ? data[0] : data;
      if (!configMap || typeof configMap !== 'object') return [];

      const widgetsKey = `customWidgets_${username}`;
      const rawWidgets = configMap[widgetsKey];

      if (!rawWidgets) {
        const matchingKeys = Object.keys(configMap).filter(k =>
          k.toLowerCase().includes('widget') || k.toLowerCase().includes('custom')
        );
        console.warn('[useUserWidgets] Key "' + widgetsKey + '" not found. Widget-related keys:', matchingKeys);
        return [];
      }

      const converted = convertDynamoDBToJS(rawWidgets);
      if (!converted) return [];
      if (Array.isArray(converted)) {
        console.log('[useUserWidgets] Extracted', converted.length, 'widgets');
        return converted;
      }
      if (typeof converted === 'object') return [converted];
      return [];
    } catch (err) {
      console.error('[useUserWidgets] Error extracting widgets from response:', err);
      return [];
    }
  }, [convertDynamoDBToJS]);

  /**
   * Fetch the user's widget configurations from the API.
   * In mock mode, returns fake widgets immediately (no API call).
   */
  const fetchWidgets = useCallback(async () => {
    // ── Mock mode: skip API, use fake data ──
    if (MOCK_MODE) {
      console.log('[useUserWidgets] 🧪 MOCK MODE — using fake widgets');
      const parsed = parseWidgets(MOCK_WIDGETS);
      setWidgets(parsed);
      setLoading(false);
      setError(null);
      return;
    }

    // ── Production mode: call the real API ──
    if (!username) {
      setLoading(false);
      setWidgets([]);
      return;
    }

    // Set loading once before the first attempt and only clear it when truly done
    setLoading(true);
    setError(null);

    const attempt = async (retryCount = 0) => {
      try {
        const data = await getUserConfigurations(AUTH_USER_POOL_ID, username);
        const rawWidgets = extractWidgetsFromResponse(data, username);
        const parsed = parseWidgets(rawWidgets);
        setWidgets(parsed);
        setLoading(false);
      } catch (err) {
        const status = err?.response?.status;

        // 401 = cookies not ready yet — retry up to 3 times with back-off.
        // Keep loading=true while waiting so the UI stays in the spinner state.
        if (status === 401 && retryCount < 3) {
          const delay = (retryCount + 1) * 1500; // 1.5s, 3s, 4.5s
          console.warn(`[useUserWidgets] 401 — retrying in ${delay}ms (attempt ${retryCount + 1}/3)`);
          setTimeout(() => attempt(retryCount + 1), delay);
          return;
        }

        console.error('[useUserWidgets] Error fetching user configurations:', err);
        if (err?.response) {
          console.error('[useUserWidgets] Response status:', status);
          console.error('[useUserWidgets] Response data:', err.response.data);
        }
        setError(err?.message || 'Não foi possível carregar os widgets');
        setWidgets([]);
        setLoading(false);
      }
    };

    attempt();
  }, [username, extractWidgetsFromResponse, parseWidgets]);

  // Fetch on mount and whenever the username changes
  useEffect(() => {
    fetchWidgets();
  }, [fetchWidgets]);

  return {
    widgets,
    loading,
    error,
    refetch: fetchWidgets,
  };
};

export default useUserWidgets;
